import { useEffect, useState } from "react";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Save, RotateCcw, ShieldCheck, Eye, Pencil, CheckCircle2 } from "lucide-react";

const CAPS = [["view", "Ver", Eye], ["edit", "Editar", Pencil], ["approve", "Aprobar", CheckCircle2]];

export default function Roles() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [role, setRole] = useState("buyer");
  const [perms, setPerms] = useState({});
  const [dirty, setDirty] = useState(false);
  const [q, setQ] = useState("");
  const isAdmin = user?.role === "admin";

  const load = () => api.get("/rbac").then((r) => { setData(r.data); setPerms(r.data.permissions[role] || {}); setDirty(false); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (data) { setPerms(data.permissions[role] || {}); setDirty(false); } }, [role, data]);

  const toggle = (mid, cap) => {
    if (!isAdmin) return;
    setPerms((p) => {
      const cur = { ...(p[mid] || { view: true, edit: false, approve: false }) };
      cur[cap] = !cur[cap];
      if ((cap === "edit" || cap === "approve") && cur[cap]) cur.view = true;
      if (cap === "view" && !cur.view) { cur.edit = false; cur.approve = false; }
      return { ...p, [mid]: cur };
    });
    setDirty(true);
  };
  const setAll = (cap, value) => {
    setPerms((p) => Object.fromEntries(data.modules.map((m) => {
      const cur = { ...(p[m.id] || {}), [cap]: value };
      if (value && cap !== "view") cur.view = true;
      if (!value && cap === "view") { cur.edit = false; cur.approve = false; }
      return [m.id, cur];
    })));
    setDirty(true);
  };
  const save = async () => {
    try { await api.put(`/rbac/roles/${role}`, { modules: perms }); toast.success("Permisos guardados"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const reset = async () => {
    try { await api.post(`/rbac/roles/${role}/reset`); toast.success("Permisos restablecidos"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <div className="p-6 text-xs text-muted-foreground">Cargando…</div>;
  const roles = data.roles.filter((r) => r.editable);
  const modules = data.modules.filter((m) => !q || m.label.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="roles-page">
      <PageHeader title="Roles y permisos" subtitle="Define qué puede ver, editar y aprobar cada rol en cada módulo. El administrador siempre tiene acceso total.">
        {isAdmin && (
          <>
            <Button size="sm" variant="outline" className="rounded-none text-xs font-bold" onClick={reset} data-testid="roles-reset-button">
              <RotateCcw size={12} className="mr-1" /> Restablecer rol
            </Button>
            <Button size="sm" className="rounded-none text-xs font-bold" onClick={save} disabled={!dirty} data-testid="roles-save-button">
              <Save size={12} className="mr-1" /> Guardar cambios
            </Button>
          </>
        )}
      </PageHeader>

      <div className="flex flex-wrap gap-1 mb-3" data-testid="roles-tabs">
        {roles.map((r) => (
          <button key={r.id} onClick={() => setRole(r.id)} data-testid={`roles-tab-${r.id}`}
            className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider border transition-colors ${role === r.id ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted"}`}>
            {r.label}
          </button>
        ))}
        <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-muted-foreground px-2"><ShieldCheck size={12} /> Administrador: acceso total (no editable)</span>
      </div>

      <div className="border bg-card p-3 mb-3 flex flex-wrap gap-2 items-center text-[11px]">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrar módulos…" className="rounded-none h-8 text-xs w-56" data-testid="roles-filter" />
        {isAdmin && CAPS.map(([cap, label]) => (
          <span key={cap} className="inline-flex items-center gap-1 border px-2 py-1">
            <span className="font-bold uppercase tracking-wider text-[10px]">{label}:</span>
            <button className="text-primary font-bold hover:underline" onClick={() => setAll(cap, true)} data-testid={`roles-all-${cap}-on`}>todos</button>
            <span className="text-muted-foreground">/</span>
            <button className="text-muted-foreground font-bold hover:underline" onClick={() => setAll(cap, false)} data-testid={`roles-all-${cap}-off`}>ninguno</button>
          </span>
        ))}
        <span className="text-muted-foreground ml-auto">Ver = aparece en el menú · Editar = alta/cambios (API) · Aprobar = autorizar/rechazar (API) · <span className="text-amber-700 font-bold">•</span> = cambiado respecto al permiso base</span>
      </div>

      <div className="tbl-wrap" data-testid="roles-matrix">
        <table className="tbl">
          <thead><tr><th>Módulo</th>{CAPS.map(([cap, label, Icon]) => <th key={cap} className="text-center w-28"><span className="inline-flex items-center gap-1"><Icon size={11} /> {label}</span></th>)}</tr></thead>
          <tbody>
            {modules.map((m) => {
              const p = perms[m.id] || { view: true, edit: false, approve: false };
              return (
                <tr key={m.id} data-testid={`roles-row-${m.id}`}>
                  <td className="text-xs font-bold">{m.label} <span className="text-[10px] text-muted-foreground font-normal font-mono">{m.id}</span></td>
                  {CAPS.map(([cap]) => {
                    const changed = data.defaults?.[role]?.[m.id] && !!data.defaults[role][m.id][cap] !== !!p[cap];
                    return (
                    <td key={cap} className={`text-center ${changed ? "bg-amber-50 dark:bg-amber-950/30" : ""}`} title={changed ? "Modificado respecto al permiso base del rol" : ""}>
                      <input type="checkbox" checked={!!p[cap]} disabled={!isAdmin} onChange={() => toggle(m.id, cap)}
                        className="h-4 w-4 accent-[hsl(var(--primary))] cursor-pointer disabled:cursor-not-allowed"
                        data-testid={`roles-cb-${m.id}-${cap}`} />
                      {changed && <span className="ml-1 text-[9px] font-bold text-amber-700 align-middle" data-testid={`roles-changed-${m.id}-${cap}`}>•</span>}
                    </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
